import { useEffect, useMemo, useRef } from "react";
import { usePermission } from "@/hooks/usePermission";
import { useGoogleTranslateRefresh } from "@/hooks/useGoogleTranslateRefresh";
import { EntityEditTabBar } from "@/components/ui/EntityEditTabBar";
import { AboutYourselfTab } from "./tabs/AboutYourselfTab";
import { InviteTab } from "./tabs/InviteTab";
import { PropertyTab } from "./tabs/PropertyTab";
import { IncomeEmploymentTab } from "./tabs/IncomeEmploymentTab";
import { PersonalHistoryTab } from "./tabs/PersonalHistoryTab";
import { SignaturePaymentTab } from "./tabs/SignaturePaymentTab";
import { NotesTab } from "./tabs/NotesTab";
import { CreditReportTab } from "./tabs/CreditReportTab";
import { ALL_APPLICATION_TABS } from '../constants'
import { useChecklistAwareTabStripWidth } from '../hooks/useChecklistAwareTabStripWidth'
import type { TabType, ApplicationTabsProps } from '../types/components'

export function ApplicationTabs({
  selectedTab,
  onTabChange,
  applicationAllData,
  applicationData,
  applicantId,
  isEdit = false,
  onFormChange,
  onRefresh,
  aboutYourselfExposeInlineValidation = false,
  propertyExposeInlineValidation = false,
  incomeExposeInlineValidation = false,
  onOpenRentChangePopup,
  hasContractData = false,
  showChecklist = false,
}: ApplicationTabsProps) {
  const { hasPolicy } = usePermission();
  const tabStripRef = useRef<HTMLDivElement>(null)
  const { maxWidth: tabStripMaxWidth, scrollTabs } = useChecklistAwareTabStripWidth(
    tabStripRef,
    showChecklist,
  )

  const tabs = useMemo(
    () => ALL_APPLICATION_TABS.filter((tab) => hasPolicy([tab.policy])),
    [hasPolicy],
  );

  useEffect(() => {
    const visibleIds = tabs.map((t) => t.id);
    if (selectedTab && !visibleIds.includes(selectedTab) && tabs.length > 0) {
      onTabChange((tabs[0]?.id ?? "aboutyourselfinfo") as TabType);
    }
  }, [tabs, selectedTab, onTabChange]);

  const activeTabMeta = useMemo(
    () => tabs.find((t) => t.id === selectedTab),
    [tabs, selectedTab],
  );

  // Admin application detail tabs swap content in-place (no route change). Match Angular by
  // forcing a new translateHtml pass whenever the visible section changes.
  useGoogleTranslateRefresh(selectedTab, isEdit);

  const entityTabs = useMemo(
    () =>
      tabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        testId: `application-detail-tab-${tab.id}-button`,
      })),
    [tabs],
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case "inviteinfo":
        return (
          <InviteTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            isEdit={isEdit}
            onFormChange={onFormChange}
            exposeInlineValidation={aboutYourselfExposeInlineValidation}
          />
        );
      case "aboutyourselfinfo":
        return (
          <AboutYourselfTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            applicantId={applicantId}
            isEdit={isEdit}
            onFormChange={onFormChange}
            exposeInlineValidation={aboutYourselfExposeInlineValidation}
          />
        );
      case "currentaddressinfo":
        return (
          <AboutYourselfTab
            view="address"
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            applicantId={applicantId}
            isEdit={isEdit}
            onFormChange={onFormChange}
            exposeInlineValidation={aboutYourselfExposeInlineValidation}
          />
        );
      case "propertyinfo":
        return (
          <PropertyTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            applicantId={applicantId}
            isEdit={isEdit}
            onFormChange={onFormChange}
            exposePropertyInlineValidation={propertyExposeInlineValidation}
            onOpenRentChangePopup={onOpenRentChangePopup}
            hasContractData={hasContractData}
          />
        );
      case "employmentinfo":
        return (
          <IncomeEmploymentTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            applicantId={applicantId}
            isEdit={isEdit}
            onFormChange={onFormChange}
            exposeIncomeInlineValidation={incomeExposeInlineValidation}
            onRefresh={onRefresh}
          />
        );
      case "questioninfo":
        return (
          <PersonalHistoryTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
          />
        );
      case "signatureinfo":
        return (
          <SignaturePaymentTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            applicantId={applicantId}
            onRefresh={onRefresh}
          />
        );
      case "notesinfo":
        return (
          <NotesTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            applicantId={applicantId}
            onRefresh={onRefresh}
          />
        );
      case "creditinfo":
        return (
          <CreditReportTab
            applicationAllData={applicationAllData}
            applicationData={applicationData}
            applicantId={applicantId}
            onRefresh={onRefresh}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mt-5 min-w-0" data-testid="application-detail-tabs">
      <div
        className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
        data-testid="application-detail-tabs-card"
      >
        <div
          ref={tabStripRef}
          className={`min-w-0 transition-[max-width] duration-200 ease-out${
            showChecklist && scrollTabs
              ? ' overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]'
              : ''
          }`}
          style={
            showChecklist &&
            tabStripMaxWidth != null &&
            tabStripMaxWidth > 0
              ? { maxWidth: tabStripMaxWidth }
              : undefined
          }
          data-testid="application-detail-tab-strip"
        >
          <EntityEditTabBar
            navTestId="application-detail-tab-nav"
            ariaLabel="Application sections"
            tabs={entityTabs}
            activeTab={selectedTab}
            onSelect={(id) => onTabChange(id as TabType)}
            align={showChecklist && scrollTabs ? 'start' : 'distribute'}
            wrapClassName={
              showChecklist
                ? scrollTabs
                  ? 'overflow-x-visible px-1 sm:px-2'
                  : 'px-1 sm:px-2'
                : undefined
            }
          />
        </div>

        <div
          role="tabpanel"
          id="application-detail-tabpanel"
          data-testid={`application-detail-tabpanel-${selectedTab}`}
          aria-label={
            activeTabMeta?.label
              ? `${activeTabMeta.label} — application details`
              : "Application details"
          }
          className={
            showChecklist
              ? 'min-h-[450px] min-w-0 bg-slate-50/50 px-2 sm:px-3 py-4 sm:py-6'
              : 'min-h-[450px] min-w-0 bg-slate-50/50 p-4 sm:p-6'
          }
        >
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
